import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection, doc, setDoc, getDoc, query, where, onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../utils/firebase';
import { apiGet, apiPost, apiPatch, apiPut, apiDelete, acceptInviteToken } from '../utils/api';
import { reportError } from '../utils/errorReporting';
import { defaultTreeName, needsStarterTree, chooseActiveTree } from '../utils/helpers';
import { peekInviteToken, clearInviteToken } from '../utils/inviteLink';
import { track, sizeBucket } from '../utils/analytics';
import { mark, markContext, finishLoadSession } from '../utils/timing';
import { useAuth } from './AuthContext';

const TreeContext = createContext(null);
export function useTree() { return useContext(TreeContext); }

// Separate context for people + attachments so that frequent Firestore data updates
// don't trigger re-renders in components that only need tree metadata.
const TreePeopleContext = createContext(null);
export function useTreePeople() { return useContext(TreePeopleContext); }

// users/{uid}/trees is a denormalized cache that makes login an O(k) lookup.
// A failed write costs a slower next login, not correctness — but swallowing it
// meant a systematic failure would silently degrade every login forever.
const indexWriteFailed = (op) => (e) =>
  console.warn(`[treeIndex] ${op} write failed; next login falls back to a scan`, e);

// Stable empty set returned for non-contributors — avoids allocating a new Set on every render.
const EMPTY_SET = new Set();

// How long the local data may disagree with the tree's counters before a fresh
// snapshot is fetched. Long enough that an in-flight write settling isn't
// mistaken for a divergence, short enough that a collaborator's delete doesn't
// linger. Nothing on the interactive path waits on this — a client applies its
// own deletes locally the moment the API confirms them.
const RECONCILE_DEBOUNCE_MS = 4000;

// Consecutive reconciliations allowed before the client gives up and leaves the
// mismatch alone. A counter that stays wrong — drift the scheduled job has yet
// to correct, or a legacy attachmentCount that never matched — would otherwise
// have every open tab refetching the whole tree every few seconds, forever.
const MAX_RECONCILE_ATTEMPTS = 3;

// Deadlines for the two requests that stand between a sign-in and a rendered
// tree. Nothing on this path may wait indefinitely.
//
// The failure that produced these was not an error. On 2026-09-24 an iPad sat
// for thirty seconds with a healthy connection, a backend answering in 25ms,
// and a client that issued no request at all, because a layer beneath it had
// not become usable. There was no rejection to catch and no status to report,
// so every guard already in this file — the per-read tolerance, the bootstrap
// backstop, the snapshot's own catch — was waiting on something that was never
// going to settle. A deadline is the only guard that works against that shape.
//
// Both sit inside the 20s watchdog in utils/timing.js, and deliberately so.
// Worst case here is 8 + 10, so a timeout always reports itself as a failure
// with a cause attached, and `stalled` stays reserved for something neither of
// these anticipated.
//
// Generous rather than tight: the cost of being wrong in one direction is a
// person on a bad train connection being told to retry something that would
// have worked, and in the other it is the spinner that started all this.
const DISCOVERY_DEADLINE_MS = 8000;
const SNAPSHOT_DEADLINE_MS = 10000;

// Snapshot refetches allowed to re-bound a rejected delta listener before the
// client stops trying. The expected cause is benign — a tab open past the read
// window in firestore.rules, which one refetch fixes — but a listener rejected
// for any *other* reason would refetch forever without a cap. Reset whenever a
// listener actually attaches.
const MAX_LISTENER_RECOVERIES = 3;

// The snapshot endpoint sends timestamps as ISO-8601 strings; the delta
// listener delivers Firestore Timestamp objects for the same fields. Both feed
// the same state, so one shape has to win, and it has to be the string — that
// is what an HTTP response can carry, and from commit 10, what a cached GCS
// object holds. Only createdAt/updatedAt are timestamps and both are top-level,
// so this doesn't recurse; the server's encoder does, defensively.
function normalizeRecord(data) {
  const out = {};
  for (const [key, value] of Object.entries(data)) {
    out[key] = value && typeof value.toDate === 'function'
      ? value.toDate().toISOString()
      : value;
  }
  return out;
}

// Attachments are held grouped by personId, which is how the detail panel wants
// them. Both the snapshot and the delta listener merge through here so the two
// paths cannot drift in how they shape the same record.
function mergeAttachment(byPerson, record) {
  const list = byPerson[record.personId] || [];
  const idx = list.findIndex(a => a.id === record.id);
  if (idx >= 0) {
    const next = [...list];
    next[idx] = record;
    byPerson[record.personId] = next;
  } else {
    byPerson[record.personId] = [...list, record];
  }
}

function removeAttachment(byPerson, personId, attachmentId) {
  const list = byPerson[personId];
  if (!list) return;
  const filtered = list.filter(a => a.id !== attachmentId);
  if (filtered.length > 0) byPerson[personId] = filtered;
  else delete byPerson[personId];
}

// Resizes and re-encodes an image file to JPEG before upload.
// Returns a new File whose .size reflects the compressed byte count.
function compressImage(file, maxDimension = 2000, quality = 0.8) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width >= height) {
          height = Math.round((height / width) * maxDimension);
          width  = maxDimension;
        } else {
          width  = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          const baseName = file.name.replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); }; // fall back to original
    img.src = url;
  });
}

// Extensions whose browser-reported MIME type is unreliable (Office documents
// especially — commonly comes back as generic 'application/octet-stream'
// depending on OS/browser), mapped to the exact type the API's validate_upload
// expects. (This said storage.rules until commit 13c closed it to clients; the
// allowed list moved into the API tier with everything else.)
const EXTENSION_CONTENT_TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv',
  // Audio the browser often reports with no type at all (WMA and AMR on a Mac
  // or iPhone). Any audio/* type is accepted by the API, which looks inside
  // the file and converts what doesn't play everywhere (api/audio.py).
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  flac: 'audio/flac',
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  wma: 'audio/x-ms-wma',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
  amr: 'audio/amr',
  caf: 'audio/x-caf',
};

const ALLOWED_CONTENT_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
]);

// Resolves the content type to declare on upload. Only trusts the browser's
// file.type when it's already one the API recognizes (or an image/
// video/audio type, which browsers detect reliably); otherwise falls back to
// an extension-based guess so a real .xlsx isn't rejected just because the
// browser reported it as application/octet-stream.
function resolveContentType(file) {
  const type = file.type;
  if (type && (/^(image|video|audio)\//.test(type) || ALLOWED_CONTENT_TYPES.has(type))) {
    return type;
  }
  const ext = file.name.split('.').pop()?.toLowerCase();
  return EXTENSION_CONTENT_TYPES[ext] || type;
}

export function TreeProvider({ children }) {
  const { user, reloadUser } = useAuth();

  // All trees the user has access to
  const [userTrees, setUserTrees]         = useState([]); // [{ treeId, name, role }]
  const [activeTreeId, setActiveTreeId]   = useState(null);
  const [treeMembers, setTreeMembers]     = useState({});
  const [memberDetails, setMemberDetails] = useState({});
  const [pendingInvites, setPendingInvites] = useState({}); // { email: roleEntry }
  const [inviteJoinError, setInviteJoinError] = useState('');
  const [people, setPeople]               = useState({});
  const [attachments, setAttachments]     = useState({});
  const [peopleCount, setPeopleCount]     = useState(0);
  const [storageBytes, setStorageBytes]   = useState(0);
  const [loadingTree, setLoadingTree]     = useState(true);
  const [inviteNotifications, setInviteNotifications] = useState([]); // [{ treeId, treeName, role }]

  // Ref so the real-time invite listener can always see current userTrees without stale closure
  const userTreesRef   = useRef([]);
  useEffect(() => { userTreesRef.current = userTrees; }, [userTrees]);
  // Read by the bootstrap effect, which runs downstream of several awaits and
  // cannot trust the activeTreeId it captured when it started.
  useEffect(() => { activeTreeIdRef.current = activeTreeId; }, [activeTreeId]);

  // Bumped to force a fresh snapshot fetch for the tree already open. Only the
  // reconciliation effect does this; see the comment there.
  const [snapshotEpoch, setSnapshotEpoch] = useState(0);
  // Which tree the snapshot effect has finished loading, success or failure.
  // The bootstrap effect below raises the spinner on every run but cannot lower
  // it, and the snapshot effect that can is keyed on activeTreeId — so this is
  // how the bootstrap tells "a load is coming" from "nothing is coming".
  const activeTreeIdRef = useRef(null);
  const snapshotSettledForRef = useRef(null);
  const reconcileAttemptsRef = useRef(0);
  // Per listener, not shared. See the note on `attached` below.
  const listenerRecoveryRef = useRef({});
  // The attempt budget is per tree. It is deliberately not reset on a successful
  // refetch — that would make the cap unreachable and hand a permanently wrong
  // counter an endless refetch loop. Only agreement (or a different tree) clears
  // it.
  useEffect(() => {
    reconcileAttemptsRef.current = 0;
    listenerRecoveryRef.current = {};
  }, [activeTreeId]);


  // Derived from userTrees + activeTreeId
  const activeTree = userTrees.find(t => t.treeId === activeTreeId) || null;
  const userRole   = activeTree?.role || null;

  /**
   * Redeems an invite link (docs/invite-link-plan.md) and files the result the
   * same way an accepted banner invitation is filed.
   *
   * The index write is the part that is easy to leave out and expensive to
   * omit. Bootstrap only runs the `memberIds array-contains` query when the
   * users/{uid}/trees index is *empty*; once a user has any tree indexed, every
   * later login trusts that index alone. So a tree joined without being written
   * there is visible for exactly one session and gone at the next login — which
   * is invisible in testing, because a brand-new account has an empty index and
   * takes the query path that finds it anyway.
   */
  const acceptInviteLink = useCallback(async (token) => {
    if (!user) return null;

    const { treeId, role } = await acceptInviteToken(token);

    let treeName = 'Family Tree';
    try {
      const treeSnap = await getDoc(doc(db, 'trees', treeId));
      treeName = treeSnap.data()?.name || treeName;
    } catch {
      // The name is cosmetic here; the membership write already succeeded.
    }

    setDoc(doc(db, 'users', user.uid, 'trees', treeId), { name: treeName, role })
      .catch(indexWriteFailed('acceptLink'));

    // The server marks the account's email verified on redemption, and the
    // client cannot see that until the token is refreshed: reload() updates the
    // local user object but not the JWT, and every write is authorized from the
    // JWT's claim. Without this the invitee sits in the tree they just joined
    // and is refused on the first edit, for a reason that is no longer true.
    reloadUser().catch(() => {
      // Not fatal: the claim is correct server-side and the next token rotation
      // will pick it up. Nothing here should undo a successful join.
    });

    setUserTrees(prev => {
      if (prev.find(t => t.treeId === treeId)) return prev;
      const updated = [...prev, { treeId, name: treeName, role }];
      userTreesRef.current = updated;
      return updated;
    });

    track('invite_accepted', { via: 'link', role });
    return { treeId, role, name: treeName };
  }, [user, reloadUser]);

  // ── On user change, collect ALL accessible trees ────────────────────────────
  useEffect(() => {
    if (!user) {
      setUserTrees([]); setActiveTreeId(null);
      setTreeMembers({}); setMemberDetails({});
      setPeople({}); setAttachments({}); setLoadingTree(false);
      setPeopleCount(0); setStorageBytes(0);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingTree(true);
      mark('discoveryStart');
      const treesRef = collection(db, 'trees');
      const allTrees = [];    // accumulates { treeId, name, role }

      // ── 1 & 2: Discover accessible trees ────────────────────────────────────
      //
      // One API call, where this used to be four Firestore reads made from the
      // browser: the users/{uid}/trees index, an invitations query, and a
      // validating read per cached entry, plus the index writes that repaired
      // them. All of it is trees.list_trees now.
      //
      // It moved because it was the last thing between a sign-in and a rendered
      // tree that the page did entirely by itself, and on 2026-09-24 that was
      // measured costing an iPad thirty seconds. The trace is in
      // src/utils/firebase.js, along with why the local cache went with it: a
      // Firestore session answering in 25ms and a client that asked it for
      // nothing, because the persistence layer underneath had not become
      // usable. Dropping that cache fixes the stall; moving this stops first
      // paint depending on the browser's database at all.
      //
      // The second reason is that those reads were invisible. Firestore traffic
      // from a page never reaches Cloud Run, which is why diagnosing that
      // session needed an instrumented client and a cabled iPad. This is an
      // ordinary request with a duration and a status on it.
      //
      // A deadline, because the failure this replaces was not an error but a
      // wait. Without one the app has no answer to a request that never
      // settles, which is exactly the shape of what went wrong: no rejection to
      // catch, no status to report, just a spinner. Long enough that a slow
      // connection still succeeds, short enough that somebody is told something
      // before they give up.
      //
      // A refusal is not the same as an emptiness. The two are kept apart below
      // rather than collapsed into "found nothing", because minting a starter
      // tree on the second would give somebody who already has one a second,
      // empty tree.
      let discoveryRefused = false;
      let pendingInvites = [];

      const deadline = new AbortController();
      const deadlineTimer = setTimeout(() => deadline.abort(), DISCOVERY_DEADLINE_MS);
      try {
        const listed = await apiGet('/api/trees', { signal: deadline.signal });
        (listed?.trees || []).forEach(t => allTrees.push(t));
        pendingInvites = listed?.pendingInvites || [];
      } catch (e) {
        discoveryRefused = true;
        const timedOut = e?.name === 'AbortError';
        console.warn(`[tree] Discovery ${timedOut ? 'timed out' : 'failed'}; continuing`, e);
        reportError(e, `tree:discovery:${timedOut ? 'deadline' : 'failed'}`);
      } finally {
        clearTimeout(deadlineTimer);
      }


      // 3. An invitation link, redeemed here rather than in a component.
      //
      // **This runs before the pending-invite loop below, and the order is the
      // fix for a race that fired on every single redemption.** Following a link
      // leaves two records pointing at the same tree: the treeInvites document
      // the invitation created, and the token. With the loop first, both were
      // acted on, and which one lost depended on the account:
      //
      //   - an unverified account (every password signup) had its loop accept
      //     refused 403, because /invites/accept requires a verified email and
      //     redeeming the link is what verifies it; the flow worked, but every
      //     redemption fired a guaranteed-failing request first
      //   - a verified account had the loop *succeed*, so the redemption that
      //     followed hit "You are already a member of this tree" and put that
      //     in front of the user as an error, at the end of a flow that had in
      //     fact just worked
      //
      // Redeeming first collapses both: the tree is in `allTrees` before the
      // loop runs, and the loop already skips trees it finds there. The stale
      // treeInvites document the server deletes mid-transaction is exactly what
      // that skip is for — the invitation list was fetched before any of this.
      //
      // It also has to stay ahead of the starter-tree decision below, or the
      // two fight: bootstrap would find no trees, mint an empty one, and open
      // it — which is what an invited user was seeing at the end of a flow they
      // followed specifically to reach someone else's tree.
      let joinedViaLink = null;
      const inviteToken = peekInviteToken();
      if (inviteToken) {
        try {
          const joined = await acceptInviteLink(inviteToken);
          if (joined) {
            joinedViaLink = joined.treeId;
            if (!allTrees.find(t => t.treeId === joined.treeId)) {
              allTrees.push({ treeId: joined.treeId, name: joined.name, role: joined.role });
            }
          }
        } catch (e) {
          // Already used, expired, already a member — all worth saying out
          // loud. The token is dropped either way; the server has answered and
          // retrying on every render would only repeat the same answer.
          setInviteJoinError(e?.message || 'That invitation link could not be used.');
        }
        clearInviteToken();
      }

      if (cancelled) return;

      // 3b. Pending invites — data already fetched in parallel with step 1
      // above, and skipped for any tree already joined, including one joined by
      // the link redeemed just above.
      // Acceptance is one server call per invite: it validates the granted role
      // against the tree's pendingInvites, joins the tree, and clears both
      // records in a single transaction. This used to be a read-modify-write of
      // the whole pendingInvites map per invite, racing every other membership
      // write, with a separate branch for the already-a-member case.
      //
      // The name arrives with the invitation now. This used to accept, then
      // read trees/{id} from the browser to find out what it had just joined,
      // which put another round trip on the bootstrap path for the benefit of
      // one string the server already had in hand.
      if (!cancelled && pendingInvites.length) {
        for (const invite of pendingInvites) {
          const invitedTreeId = invite.treeId;
          if (allTrees.find(t => t.treeId === invitedTreeId)) continue;
          try {
            const { role } = await apiPost(
              `/api/trees/${encodeURIComponent(invitedTreeId)}/invites/accept`,
            );
            allTrees.push({
              treeId: invitedTreeId,
              name: invite.name || 'Family Tree',
              role,
            });
          } catch (e) {
            console.warn('Could not accept invite for tree', invitedTreeId, e);
          }
        }
      }

      if (cancelled) return;

      // 4. No trees at all → create a personal default tree.
      //
      // This used to fire whenever the user *owned* none, which meant someone
      // who arrived by invitation got an empty tree of their own minted beside
      // the one they were invited to — and, because the active-tree preference
      // below favours an owned tree, landed in the empty one. The tree they
      // were invited to see was in the switcher, one click away, entirely
      // unmentioned. Someone joining a family tree is not asking to start one;
      // they can create theirs whenever they want to.
      // The refusal guard matters most here. "No trees found" and "could not
      // find out" look identical from allTrees, and minting a starter tree on
      // the second would give somebody who already has a tree a second empty
      // one, or hand an invited member a tree of their own beside the one they
      // joined. The server's one-owned-tree cap would refuse most of that, but
      // relying on a cap to undo a decision this code should not have made is
      // the wrong place to catch it.
      if (needsStarterTree(allTrees) && !discoveryRefused) {
        const newTreeName = defaultTreeName(user.displayName);
        try {
          // The server mints the id, keeping the deterministic {uid}_tree form
          // for a first tree so existing accounts are unaffected.
          const { treeId: newId } = await apiPost('/api/trees', { name: newTreeName });
          allTrees.push({ treeId: newId, name: newTreeName, role: 'owner' });
          // `via` separates the tree the app mints on a first login from one the
          // user asked for: both are trees created, but only the second is a
          // decision, and a funnel that cannot tell them apart reads as though
          // every account starts a tree deliberately.
          track('tree_created', { tree_count: allTrees.length, via: 'starter' });
        } catch (e) {
          // Deliberately not fatal. Everything below — setUserTrees, the index
          // writes, the active-tree default — used to sit downstream of an
          // unguarded await, so one refused POST left loadingTree true forever
          // and the user looked at a loading screen rather than a tree.
          //
          // It matters more now that the server allows one owned tree per
          // account: discovery skips a tree it cannot read (see the per-document
          // catch above), so an account that *does* own one can reach here and
          // be refused. Carrying on with whatever was discovered is right in
          // both cases.
          console.warn('Could not create a starter tree; continuing without one', e);
        }
      }

      // The users/{uid}/trees index is maintained by trees.list_trees now, which
      // seeds and prunes it as part of answering. Doing it from here meant the
      // repair only happened when somebody opened the app on a network good
      // enough to finish the job, and it put two more writes on a path whose
      // whole problem was how much it asked of the browser.

      setUserTrees(allTrees);
      userTreesRef.current = allTrees; // sync ref immediately so listener sees up-to-date list
      // Only a *default*, and it must not overwrite a choice already made.
      //
      // Bootstrap is slow and asynchronous; redeeming an invite link finishes
      // inside it and switches to the tree the link was for. Setting this
      // unconditionally undid that a few hundred milliseconds later and put an
      // invited user back in their own tree — twice, because the first attempt
      // to fix it made the invite switch wait for the tree to appear in the
      // switcher, which it already did. Ordering cannot be won here: whichever
      // of the two runs second must be the one that yields, and the functional
      // update is what makes that true in both directions.
      mark('discoveryEnd');
      markContext({ treeCount: allTrees.length, discoveryRefused });

      const preferred = chooseActiveTree(allTrees, joinedViaLink);
      if (preferred) setActiveTreeId(current => current || preferred.treeId);
      mark('treeChosen');

      // Lower the spinner this run raised, unless a snapshot load is on its way
      // to lower it instead.
      //
      // This effect sets loadingTree true at the top of every run, and the only
      // code that sets it false is the snapshot effect, which is keyed on
      // activeTreeId. That pairing holds on a first run, where the line above
      // is a real change and the snapshot effect follows. It breaks on a re-run
      // that lands on the tree already being shown: the functional update
      // returns the identical id, React bails out, the snapshot effect does not
      // re-run, and the spinner stays up for good with no network activity to
      // explain it. Observed 2026-09-13 on a new account, where registration
      // and the wizard's forced token refresh re-render AuthProvider after the
      // tree has already loaded, which is what re-runs this effect.
      //
      // Two situations mean a load is coming, and only those two. A tree was
      // just chosen where there was none, so activeTreeId genuinely changed and
      // the snapshot effect is about to run. Or one is already in flight and has
      // not settled yet. In every other case, this run is the last thing that
      // will happen, and if it does not lower the spinner nothing will.
      //
      // The refused-discovery case is why this is not simply "did a snapshot
      // settle". A run that finds no trees, because it was not allowed to look,
      // chooses nothing and starts nothing. An earlier version left the spinner
      // up there, which is the original bug wearing a different hat.
      //
      // Both have to be refs rather than captured values: this code is
      // downstream of several awaits, so a value read when the run started may
      // be stale by now.
      const startingLoad = Boolean(preferred) && !activeTreeIdRef.current;
      const loadInFlight = Boolean(activeTreeIdRef.current) && !snapshotSettledForRef.current;
      if (!cancelled && !startingLoad && !loadInFlight) {
        setLoadingTree(false);
        // Bootstrap finished and no snapshot is coming, so nothing later will
        // close this measurement. Reported as its own outcome rather than left
        // to the watchdog, which would otherwise record twenty seconds of
        // nothing happening as a stall for somebody who simply has no tree.
        finishLoadSession('no-tree');
      }
    })().catch((e) => {
      // The backstop, and the reason it lowers the spinner unconditionally.
      //
      // Every read above is individually tolerant now, so arriving here means
      // something failed that was not anticipated at all. Whatever it was, it
      // happened before the setActiveTreeId at the end of the block, since that
      // is the last statement in it: no snapshot load can be on its way, and so
      // nothing else in the app is ever going to lower this spinner.
      //
      // Attached to the promise rather than written as a try around the body,
      // which is the same thing for an async function and keeps the block at
      // one indent level. An unguarded rejection here was invisible except as
      // an app that would not load: it reached the window as an
      // unhandledrejection, and the only trace was a bare "Missing or
      // insufficient permissions" with no call site attached to it.
      //
      // Somebody looking at an empty tree is better off than somebody looking
      // at a spinner. The empty tree says what state it is in and offers a way
      // forward.
      if (cancelled) return;
      console.error('[tree] Bootstrap failed', e);
      reportError(e, 'tree:bootstrap');
      setLoadingTree(false);
      finishLoadSession('failed');
    });

    return () => { cancelled = true; };
  }, [user, acceptInviteLink]);

  // ── Switch the active tree ──────────────────────────────────────────────────
  const switchTree = useCallback((id) => {
    if (id === activeTreeId) return; // already on this tree, nothing to do
    setPeople({});
    setAttachments({});
    setTreeMembers({});
    setMemberDetails({});
    setPendingInvites({});
    setPeopleCount(0);
    setStorageBytes(0);
    setLoadingTree(true);
    setActiveTreeId(id);
  }, [activeTreeId]);

  // ── Stop watching the active tree entirely ──────────────────────────────────
  // For account deletion: the one operation that destroys a tree out from under
  // a client still watching it. Without this, the live listeners and the
  // snapshot fetch keep running against documents the server is deleting, fail
  // with "Missing or insufficient permissions" and a 404, and get posted to
  // /api/client-errors — putting two entries that look like real browser
  // crashes into the log for every account deletion. Observed on the first real
  // deletion, 2026-08-29.
  //
  // Every tree listener is keyed on activeTreeId and unsubscribes in its effect
  // cleanup, so dropping it to null is what actually detaches them. Nothing
  // re-selects a tree afterwards: the bootstrap effect that picks a default only
  // runs when the user changes, and it yields to any choice already made.
  //
  // Deliberately not switchTree(null). That is about moving between trees and
  // sets loadingTree, which would leave a spinner up over a tree that no longer
  // exists; this is about there being nothing left to watch.
  const detachTree = useCallback(() => {
    setActiveTreeId(null);
    setPeople({});
    setAttachments({});
    setTreeMembers({});
    setMemberDetails({});
    setPendingInvites({});
    setPeopleCount(0);
    setStorageBytes(0);
    setLoadingTree(false);
  }, []);

  // ── Subscribe to the active tree document (member list + name) ──────────────
  useEffect(() => {
    if (!activeTreeId) return;
    const unsub = onSnapshot(doc(db, 'trees', activeTreeId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setTreeMembers(data.members || {});
        setMemberDetails(data.memberDetails || {});
        setPendingInvites(data.pendingInvites || {});
        setPeopleCount(data.peopleCount || 0);
        setStorageBytes(data.storageBytes || 0);
        // Keep the name in userTrees in sync
        setUserTrees(prev => prev.map(t =>
          t.treeId === activeTreeId ? { ...t, name: data.name || t.name } : t
        ));
      }
    });
    return unsub;
  }, [activeTreeId]);

  // ── Track tree activity for admin dead-tree detection ───────────────────────
  // Debounced client-side via localStorage (no read needed) so opening a tree
  // repeatedly in a day doesn't spam writes. Scoped to the lastActiveAt field
  // only in firestore.rules so any member — not just owner/admin — can bump it.
  useEffect(() => {
    if (!activeTreeId) return;
    const debounceKey = `lastActiveWrite:${activeTreeId}`;
    const lastWrite = Number(localStorage.getItem(debounceKey) || 0);
    if (Date.now() - lastWrite < 24 * 60 * 60 * 1000) return;
    apiPost(`/api/trees/${encodeURIComponent(activeTreeId)}/activity`)
      .then(() => localStorage.setItem(debounceKey, String(Date.now())))
      .catch((e) => console.warn('[activity] Could not record tree visit', e));
  }, [activeTreeId]);

  // The pendingInvites auto-cleanup effect that used to live here is gone.
  // It existed because accepting an invite couldn't remove its own entry — the
  // invitee had no write access to the tree beyond their own membership fields —
  // so an owner's client swept the leftovers later by rewriting the whole map.
  // That sweep was itself a read-modify-write racing every other membership
  // change. Acceptance now clears both records in one server-side transaction,
  // so there is nothing left to clean up.

  // ── Real-time listener: notify when a new invite arrives while logged in ─────
  // Depends only on [user] so it doesn't restart when switching trees.
  // userTreesRef is set synchronously at the end of the initial load, so by the
  // time legitimate invite notifications arrive it already contains accepted trees.
  useEffect(() => {
    if (!user?.email) return;
    const invitesRef = collection(db, 'treeInvites', user.email.toLowerCase(), 'trees');
    const unsub = onSnapshot(invitesRef, async (snapshot) => {
      const currentIds = new Set(userTreesRef.current.map(t => t.treeId));
      const newDocs = snapshot.docs.filter(d => !currentIds.has(d.id));
      if (newDocs.length === 0) { setInviteNotifications([]); return; }
      const notifications = await Promise.all(
        newDocs.map(async (d) => {
          const treeId = d.id;
          const { role } = d.data();
          try {
            const treeSnap = await getDoc(doc(db, 'trees', treeId));
            return { treeId, role, treeName: treeSnap.data()?.name || 'Family Tree' };
          } catch (e) {
            return { treeId, role, treeName: 'Family Tree' };
          }
        })
      );
      setInviteNotifications(notifications);
    });
    return unsub;
  }, [user]);

  // ── Load the tree, then follow it ───────────────────────────────────────────
  // One HTTP response for the bulk load, then two *bounded* listeners for the
  // live deltas.
  //
  // What this replaces was a getDocs(limit 50) for a first screenful plus an
  // unbounded onSnapshot over the whole people collection. That second listener
  // read every document in the tree, on every client, on every load — ~700
  // billed reads to open a mid-sized tree, and the single largest line in the
  // cost analysis. The server does those reads once now, and commit 10 stops it
  // doing them at all by serving a materialized copy from the CDN.
  //
  // The listeners are bounded by the snapshot's own timestamp, so attaching them
  // costs a read per document that changed since the load — normally none —
  // rather than one per document that exists.
  useEffect(() => {
    if (!activeTreeId) return;
    let cancelled = false;
    const unsubs = [];

    // A load is now in flight for this tree; nothing else may lower the spinner
    // until it settles below.
    snapshotSettledForRef.current = null;

    // People and attachments are bounded on different fields because they
    // change in different ways. A person document is updated in place, so
    // `updatedAt` moves and the listener sees every edit. An attachment is
    // immutable once finalized — created and deleted, never modified — and
    // carries no updatedAt at all, so `createdAt` is both the only usable bound
    // and an exactly correct one.
    //
    // Neither listener can report a deletion of something it isn't already
    // watching: a document that vanishes without ever entering the query's
    // result set produces no event. Own deletes are applied locally by the
    // mutation that made them; a *collaborator's* delete is what the
    // reconciliation effect below is for.
    // A delta listener does not fail only when something is broken. Commit 11
    // narrowed the read rules to documents changed within
    // READ_WINDOW_HOURS, and a client's `since` is fixed at load — so a tab
    // left open past that window has a query the rules no longer permit, and
    // Firestore rejects it. Refetching the snapshot is the cure: it returns a
    // fresh `snapshotTime` well inside the window, and the listener re-attaches
    // around it.
    //
    // Recovering matters more than it looks. The failure is otherwise
    // *silent* — an edit changes no counter, so reconciliation never notices,
    // and the tab goes on showing a tree that has quietly stopped updating.
    const recover = (label, since) => (e) => {
      if (cancelled) return;

      // What the bound actually was, because the next occurrence should not
      // need another investigation. The rules permit these queries only while
      // `since` is inside the read window, so its age is the first thing worth
      // knowing, and on 2026-09-13 it was the one thing the report did not say.
      // A denial at three hours and a denial at thirty are different bugs.
      const ageHours = since ? (Date.now() - since.getTime()) / 3600000 : null;
      const detail =
        `since=${since ? since.toISOString() : 'none'} ` +
        `age=${ageHours === null ? 'unknown' : `${ageHours.toFixed(1)}h`} ` +
        `signedIn=${Boolean(auth.currentUser)}`;

      console.error(`[tree] ${label} delta listener failed (${detail})`, e);
      reportError(e, `${label}:delta-listener ${detail}`);

      const attempts = listenerRecoveryRef.current[label] || 0;
      if (attempts >= MAX_LISTENER_RECOVERIES) {
        console.error(
          `[tree] ${label} listener has failed ${MAX_LISTENER_RECOVERIES} times; ` +
          `leaving it down rather than refetching in a loop`,
        );
        return;
      }
      listenerRecoveryRef.current[label] = attempts + 1;
      console.warn(
        `[tree] Refetching snapshot to re-bound the ${label} listener ` +
        `(recovery ${attempts + 1}/${MAX_LISTENER_RECOVERIES})`,
      );
      setSnapshotEpoch(n => n + 1);
    };

    // onSnapshot fires its success callback on attach, even with an empty
    // result set, so this runs whenever a listener is genuinely established.
    // Resetting here and not on a successful *load* is what keeps the cap
    // reachable: a load that succeeds only for the listener to fail again would
    // otherwise reset the budget every time and loop forever.
    //
    // **Per listener, which is the part that was missing.** There are two, and
    // they shared one counter, so the healthy one's attach reset the budget the
    // failing one had just spent. One listener attaching while the other is
    // refused is not an incidental case, it is exactly the shape of the
    // failure, so the cap could never reach its limit. On 2026-09-13 that
    // produced 95 snapshot requests in 43 seconds from a single tab, stopped
    // only by the server's rate limiter returning 429.
    const attached = (label) => { listenerRecoveryRef.current[label] = 0; };

    const follow = (since) => {
      unsubs.push(onSnapshot(
        query(collection(db, 'trees', activeTreeId, 'people'), where('updatedAt', '>', since)),
        (snap) => {
          if (cancelled) return;
          attached('people');
          setPeople(prev => {
            const next = { ...prev };
            snap.docChanges().forEach(change => {
              if (change.type === 'removed') delete next[change.doc.id];
              else next[change.doc.id] = { ...normalizeRecord(change.doc.data()), id: change.doc.id };
            });
            return next;
          });
        },
        recover('people', since),
      ));

      // `restricted == false` is required, not an optimisation: firestore.rules
      // only admits public attachments, and rules refuse a query that *could*
      // return anything else. Private ones this member may see come from the
      // snapshot, and their own uploads from the finalize response.
      unsubs.push(onSnapshot(
        query(
          collection(db, 'trees', activeTreeId, 'attachments'),
          where('restricted', '==', false),
          where('createdAt', '>', since),
        ),
        (snap) => {
          if (cancelled) return;
          attached('attachments');
          setAttachments(prev => {
            const next = { ...prev };
            snap.docChanges().forEach(change => {
              const record = { ...normalizeRecord(change.doc.data()), id: change.doc.id };
              if (change.type === 'removed') removeAttachment(next, record.personId, record.id);
              else mergeAttachment(next, record);
            });
            return next;
          });
        },
        recover('attachments', since),
      ));
    };

    (async () => {
      const deadline = new AbortController();
      const deadlineTimer = setTimeout(() => deadline.abort(), SNAPSHOT_DEADLINE_MS);
      try {
        mark('snapshotRequested');
        const snap = await apiGet(
          `/api/trees/${encodeURIComponent(activeTreeId)}/snapshot`,
          { signal: deadline.signal },
        );
        mark('snapshotReceived');
        if (cancelled) return;

        // Replaced wholesale, not merged. On a reconciliation refetch that is
        // the entire point: whatever the local copy is holding that the server
        // no longer has — the deleted person this refetch exists to notice —
        // goes away with it.
        const peopleById = {};
        snap.people.forEach(p => { peopleById[p.id] = p; });
        const byPerson = {};
        snap.attachments.forEach(a => mergeAttachment(byPerson, a));
        setPeople(peopleById);
        setAttachments(byPerson);
        snapshotSettledForRef.current = activeTreeId;
        setLoadingTree(false);

        // The data is in state, which is not the same as being on the screen.
        // A large tree still has to lay out and paint, and that is the part the
        // person is actually waiting through, so the report is sent from the
        // frame after React has committed rather than from here.
        markContext({
          peopleCount: snap.people.length,
          attachmentCount: snap.attachments.length,
        });
        const paint = () => { mark('treePainted'); finishLoadSession('loaded'); };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(paint);
        else paint();

        // snapshotTime is rewound by the server past any plausible clock skew,
        // so the listeners overlap the snapshot rather than risking a gap
        // between them. Re-delivered documents merge to the same state.
        follow(new Date(snap.snapshotTime));
      } catch (e) {
        // Swallowing this left the app stuck on its loading state with nothing
        // reported anywhere: setLoadingTree(false) never ran, so a failed first
        // read looked identical to a slow one.
        if (cancelled) return;
        const timedOut = e?.name === 'AbortError';
        console.error(`[tree] Snapshot load ${timedOut ? 'timed out' : 'failed'}`, e);
        reportError(e, `tree:snapshot-load${timedOut ? ':deadline' : ''}`);
        snapshotSettledForRef.current = activeTreeId;
        setLoadingTree(false);
        // Reported as its own outcome. A fast failure and a fast success are
        // the same duration, and only one of them is good news.
        finishLoadSession('failed');
      } finally {
        clearTimeout(deadlineTimer);
      }
    })();

    return () => {
      cancelled = true;
      unsubs.forEach(unsub => unsub());
    };
  }, [activeTreeId, snapshotEpoch]);

  // ── Reconcile the local copy against the tree's counters ────────────────────
  // The safety net for the one thing a bounded listener structurally cannot
  // see: a document deleted by someone else that this client was not already
  // watching. Firestore reports a removal only for documents inside the query's
  // result set, and a person nobody has touched since load is not in one.
  //
  // Rather than track deletions server-side, this leans on counters that are
  // already live and already exact — `peopleCount` on the tree document (which
  // has its own listener, and has been transactional since commit 8) and
  // `attachmentCount` on each person. When one disagrees with what is held
  // locally for longer than a moment, the snapshot is refetched.
  //
  // Both directions of mismatch count. A stale *extra* person is the delete
  // this exists to catch; a missing one means a delta went astray, and
  // refetching is the same cure.
  useEffect(() => {
    if (!activeTreeId || loadingTree) return;

    const localPeople = Object.keys(people).length;
    // attachmentCount counts only the attachments every member can see, so
    // the private ones this member happens to be allowed are left out of the
    // comparison. Counting them would make the owner, who sees every private
    // file, disagree with every person who has one.
    const attachmentsDisagree = Object.values(people).some(
      p => (p.attachmentCount || 0) !==
        (attachments[p.id] || []).filter(a => !a.restricted).length,
    );
    if (localPeople === peopleCount && !attachmentsDisagree) {
      reconcileAttemptsRef.current = 0;
      return;
    }

    if (reconcileAttemptsRef.current >= MAX_RECONCILE_ATTEMPTS) return;

    const timer = setTimeout(() => {
      reconcileAttemptsRef.current += 1;
      console.warn(
        `[tree] Local copy disagrees with tree counters (people ${localPeople}/${peopleCount}` +
        `${attachmentsDisagree ? ', attachment counts differ' : ''}); refetching snapshot ` +
        `(attempt ${reconcileAttemptsRef.current}/${MAX_RECONCILE_ATTEMPTS})`,
      );
      setSnapshotEpoch(n => n + 1);
    }, RECONCILE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [activeTreeId, loadingTree, people, attachments, peopleCount]);

  // ── CRUD: People ────────────────────────────────────────────────────────────
  // Writes go through the API tier. Reads stay on the Firestore listener above,
  // which still sees these writes land — that's what lets writes move
  // server-side without disturbing live collaboration.
  const addPerson = useCallback(async (personData) => {
    if (!activeTreeId) return null;
    // The server mints the id. It used to be chosen here, which meant a create
    // could silently overwrite an existing person by reusing their id.
    const { id } = await apiPost(
      `/api/trees/${encodeURIComponent(activeTreeId)}/people`, personData,
    );
    // peopleCount is the listener's figure and lands a moment behind the write,
    // so the count *after* this person is one more than what state holds now.
    track('person_added', { people_count_after: peopleCount + 1, via: 'manual' });
    return id;
  }, [activeTreeId, peopleCount]);

  // Creating many people at once — GEDCOM import is the only caller so far.
  // Not a convenience wrapper around addPerson: sending them one at a time is
  // one write to the shared tree document per person, which Firestore sustains
  // at about one per second, and this spends one per 400 instead. Ids come back
  // in the order the people were sent, which is what lets the importer's second
  // pass attach relationships to the right people.
  // Deliberately does not log `person_added` per person: a 600-person import
  // would send 600 events describing one action, drown every manual add in the
  // report, and count against GA4's per-session event limits for no gain.
  // `gedcom_import_completed` carries the number instead.
  const bulkCreatePeople = useCallback(async (peopleData) => {
    if (!activeTreeId || !peopleData?.length) return { ids: [] };
    return apiPost(
      `/api/trees/${encodeURIComponent(activeTreeId)}/people/bulk`,
      { people: peopleData },
    );
  }, [activeTreeId]);

  const updatePerson = useCallback(async (personId, updates) => {
    if (!activeTreeId) return;
    await apiPatch(
      `/api/trees/${encodeURIComponent(activeTreeId)}/people/${encodeURIComponent(personId)}`,
      updates,
    );
  }, [activeTreeId]);

  // Deleting a person also sweeps them out of everyone else's parentIds,
  // children and marriages, and removes their attachments and Storage objects.
  // That sweep used to run here over the browser's cached copy of the tree: it
  // missed references added since the cache was populated, clobbered concurrent
  // edits by writing back whole arrays, and exceeded Firestore's 500-document
  // batch limit on large trees. It now runs server-side against live data in
  // chunked transactions.
  //
  // The local removal afterwards is not an optimism: it is the only thing that
  // removes them from this client. The delta listener is bounded on
  // `updatedAt > snapshotTime`, and a person who was not edited since the load
  // was never in its result set, so their disappearance raises no event here.
  // The reconciliation effect would catch it seconds later; doing it directly
  // is instant, and reconciliation stays what it is meant to be — the net for
  // *other* people's deletes.
  const deletePerson = useCallback(async (personId) => {
    if (!activeTreeId) return;
    await apiDelete(
      `/api/trees/${encodeURIComponent(activeTreeId)}/people/${encodeURIComponent(personId)}`,
    );
    setPeople(prev => {
      if (!(personId in prev)) return prev;
      const next = { ...prev };
      delete next[personId];
      return next;
    });
    // The server deletes their attachments in the same call.
    setAttachments(prev => {
      if (!prev[personId]) return prev;
      const next = { ...prev };
      delete next[personId];
      return next;
    });
  }, [activeTreeId]);

  // ── Attachments ─────────────────────────────────────────────────────────────
  // Uploads are a three-step exchange: reserve quota, send the bytes straight to
  // Storage, then finalize. Reserving first is what stops concurrent uploads
  // each passing a quota check that only one of them fits — the old flow checked
  // the limit in storage.rules against a counter that only advances *after* an
  // upload completes, so uploads started together all saw the same pre-upload
  // total. The bytes still go browser-to-Storage; the API only grants
  // permission and does the accounting.
  const runUpload = useCallback(async (file, contentType, reservation) => {
    const base = `/api/trees/${encodeURIComponent(activeTreeId)}/uploads`;
    const { uploadId, storagePath, uploadUrl, uploadHeaders } = await apiPost(base, reservation);
    try {
      // Straight to Storage over a signed PUT URL the reservation issued, not
      // through the Firebase SDK. The bytes still never touch Cloud Run; what
      // changed is that the browser no longer has standing permission to write
      // to the bucket at all — storage.rules denies it, and this one-file,
      // half-hour capability is the only way in.
      //
      // The headers are sent exactly as given: content type and size range are
      // part of the signature, so altering either produces a signature mismatch
      // rather than a permissions error. No Authorization header — the
      // signature is the credential, and adding one makes Storage reject it.
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: uploadHeaders,
        body: file,
      });
      if (!response.ok) {
        throw new Error(
          `Upload failed with status ${response.status}: ${await response.text()}`,
        );
      }
    } catch (e) {
      // Release the hold immediately rather than leaving it to expire.
      await apiDelete(`${base}/${uploadId}`).catch(
        (err) => console.warn('[upload] Could not release reservation', uploadId, err),
      );
      throw e;
    }
    return apiPost(`${base}/${uploadId}/finalize`);
  }, [activeTreeId]);

  // `visibleTo` is null (or omitted) for everyone in the tree, or a list of
  // member uids for a private file — empty meaning "only me". The server adds
  // the uploader and treats the owner as always included.
  const uploadAttachment = useCallback(async (personId, file, visibleTo = null) => {
    if (!activeTreeId) return;
    const isImage = file.type.startsWith('image/');
    if (isImage) file = await compressImage(file);
    const contentType = resolveContentType(file);
    const result = await runUpload(file, contentType, {
      personId, name: file.name, contentType, size: file.size, kind: 'attachment',
      visibleTo,
    });
    // Merged here because a private upload never reaches the delta listener
    // (see the restricted filter on it). A public one also arrives that way a
    // moment later, which merges to the same state.
    if (result?.attachment) {
      setAttachments(prev => {
        const next = { ...prev };
        mergeAttachment(next, result.attachment);
        return next;
      });
    }
    // After finalize, not after the PUT: an upload whose reservation was never
    // finalized is one the tree does not have.
    if (isImage) track('photo_uploaded', { size_bucket: sizeBucket(file.size), via: 'attachment' });
    return result;
  }, [activeTreeId, runUpload]);

  // Each upload gets its own path. The path used to be a fixed
  // trees/{treeId}/photos/{personId}, so replacing a photo rewrote
  // photoStoragePath with the value it already had — useSignedUrl keys its
  // effect on that string, so it never re-ran, the signed-URL cache kept
  // handing back the previous URL for 55 minutes, and an unchanged <img src>
  // left the browser showing its cached copy. Only the first-ever upload
  // appeared to work, because that one really does change the value.
  // Each upload still lands on its own path, so replacing a photo changes
  // photoStoragePath and the render chain actually notices. Removing the object
  // being replaced is now the server's job, done on finalize, so an abandoned
  // browser can't leave an orphan behind.
  const uploadPersonPhoto = useCallback(async (personId, file) => {
    if (!activeTreeId) return null;
    file = await compressImage(file);
    const { storagePath } = await runUpload(file, 'image/jpeg', {
      personId, name: file.name, contentType: 'image/jpeg',
      size: file.size, kind: 'photo',
    });
    track('photo_uploaded', { size_bucket: sizeBucket(file.size), via: 'profile_photo' });
    return storagePath;
  }, [activeTreeId, runUpload]);

  const deletePersonPhoto = useCallback(async (personId) => {
    if (!activeTreeId) return;
    // The server resolves the stored path, including the fixed legacy location
    // used by records written before per-upload paths.
    await apiDelete(
      `/api/trees/${encodeURIComponent(activeTreeId)}/people/${encodeURIComponent(personId)}/photo`,
    );
  }, [activeTreeId]);

  // storagePath and size are still accepted so existing call sites keep working;
  // the server reads both from the attachment document rather than trusting them.
  const deleteAttachment = useCallback(async (attId) => {
    if (!activeTreeId) return;
    await apiDelete(
      `/api/trees/${encodeURIComponent(activeTreeId)}/attachments/${encodeURIComponent(attId)}`,
    );
    // Removed locally for the same reason as deletePerson: the attachment
    // listener is bounded on `createdAt > snapshotTime`, so anything uploaded
    // before this session began is not in its result set and its deletion
    // raises no event. Leaving it would also put the local count at odds with
    // the person's attachmentCount and trigger a needless refetch.
    setAttachments(prev => {
      const personId = Object.keys(prev).find(pid => prev[pid].some(a => a.id === attId));
      if (!personId) return prev;
      const next = { ...prev };
      removeAttachment(next, personId, attId);
      return next;
    });
  }, [activeTreeId]);

  // Changes who can see an attachment. Only its uploader and the tree's owner
  // may; the server enforces that. The returned record replaces the local one
  // directly, since no listener reports a change to an existing attachment.
  // Other members' tabs catch up through the attachmentCount reconciliation
  // when the file moves between public and private (see set_visibility in
  // api/attachments.py).
  const setAttachmentVisibility = useCallback(async (attId, visibleTo) => {
    if (!activeTreeId) return null;
    const { attachment } = await apiPut(
      `/api/trees/${encodeURIComponent(activeTreeId)}/attachments/${encodeURIComponent(attId)}/visibility`,
      { visibleTo },
    );
    if (attachment) {
      setAttachments(prev => {
        const next = { ...prev };
        mergeAttachment(next, attachment);
        return next;
      });
    }
    return attachment;
  }, [activeTreeId]);

  // ── Contributor: compute editable person IDs ──────────────────────────────
  // Hash only structural relationship fields so editablePersonIds doesn't
  // recompute on cosmetic edits (name, photo, occupation, etc.).
  const editableFamilyHash = useMemo(() =>
    Object.values(people).map(p =>
      `${p.id}:${(p.parentIds||[]).slice().sort().join(',')}:${(p.marriages||[]).map(m=>m.spouseId).sort().join(',')}`
    ).sort().join('|'),
    [people]
  );

  // Scope kept intentionally narrow — self, spouse(s), children (incl. a
  // spouse's children), parents, siblings — to match what firestore.rules
  // (isInContributorScope) actually enforces server-side. Nieces/nephews and
  // beyond used to be included here but were never enforced by the rules, so
  // a contributor could only edit them by bypassing the UI entirely; trimmed
  // so the UI never offers an edit the server will then reject.
  const editablePersonIds = useMemo(() => { // eslint-disable-line
    if (userRole !== 'contributor' || !user) return EMPTY_SET;
    const linkedPersonId = memberDetails[user.uid]?.linkedPersonId;
    if (!linkedPersonId || !people[linkedPersonId]) return EMPTY_SET;

    const me = people[linkedPersonId];
    const editable = new Set([linkedPersonId]);

    // Spouses (own marriages + reverse lookup)
    const spouseIds = new Set();
    (me.marriages || []).forEach(m => { if (m.spouseId && people[m.spouseId]) spouseIds.add(m.spouseId); });
    Object.values(people).forEach(p => {
      (p.marriages || []).forEach(m => { if (m.spouseId === linkedPersonId) spouseIds.add(p.id); });
    });
    spouseIds.forEach(id => editable.add(id));

    // Children — mine, and any spouse's (step-children)
    Object.values(people).forEach(p => {
      const parentIds = p.parentIds || [];
      if (parentIds.includes(linkedPersonId) || parentIds.some(pid => spouseIds.has(pid))) {
        editable.add(p.id);
      }
    });

    // Parents
    (me.parentIds || []).forEach(pid => { if (people[pid]) editable.add(pid); });

    // Siblings (share at least one parent)
    const myParentIds = me.parentIds || [];
    if (myParentIds.length > 0) {
      Object.values(people).forEach(p => {
        if (p.id === linkedPersonId) return;
        if ((p.parentIds || []).some(pid => myParentIds.includes(pid))) {
          editable.add(p.id);
        }
      });
    }

    return editable;
  }, [userRole, user, memberDetails, editableFamilyHash]); // eslint-disable-line

  // ── Member management (owner only) ─────────────────────────────────────────
  // Membership changes are single server-side field writes now. They used to be
  // read-modify-writes of whole maps on the tree document — the client rewrote
  // all of pendingInvites because Firestore field paths break on the dots and
  // '@' in an email address — so two overlapping invites meant the second write
  // was based on a pre-first snapshot and silently erased it.
  const inviteMember = useCallback(async (email, role, linkedPersonId = null) => {
    if (!activeTreeId) return null;
    // Returns the invite token so the caller can build the link that goes in
    // the email. It is a bearer credential, so it is never stored anywhere but
    // the message being composed.
    return apiPost(`/api/trees/${encodeURIComponent(activeTreeId)}/invites`, {
      email, role, linkedPersonId,
    });
  }, [activeTreeId]);

  const revokeInvite = useCallback(async (email) => {
    if (!activeTreeId) return;
    await apiDelete(
      `/api/trees/${encodeURIComponent(activeTreeId)}/invites/${encodeURIComponent(email.trim().toLowerCase())}`,
    );
  }, [activeTreeId]);

  const updateMemberRole = useCallback(async (memberId, role, linkedPersonId) => {
    if (!activeTreeId) return;
    // linkedPersonId is omitted entirely when undefined, so a plain role change
    // leaves an existing contributor link alone.
    await apiPatch(
      `/api/trees/${encodeURIComponent(activeTreeId)}/members/${encodeURIComponent(memberId)}`,
      linkedPersonId === undefined ? { role } : { role, linkedPersonId },
    );
  }, [activeTreeId]);

  const removeMember = useCallback(async (memberId) => {
    if (!activeTreeId) return;
    await apiDelete(
      `/api/trees/${encodeURIComponent(activeTreeId)}/members/${encodeURIComponent(memberId)}`,
    );
  }, [activeTreeId]);

  const dismissInviteJoinError = useCallback(() => setInviteJoinError(''), []);

  const acceptIncomingInvite = useCallback(async (treeId, treeName = 'Family Tree') => {
    if (!user) return false;
    // Preemptively add treeId to the ref so the onSnapshot listener won't re-show the toast
    // while Firestore writes are in flight.
    if (!userTreesRef.current.find(t => t.treeId === treeId)) {
      userTreesRef.current = [...userTreesRef.current, { treeId, role: 'viewer', name: treeName }];
    }
    // Clear the notification immediately
    setInviteNotifications(prev => prev.filter(n => n.treeId !== treeId));

    try {
      // The server reads the granted role from the tree's pendingInvites rather
      // than from the treeInvites document, since any verified user can write
      // the latter for an arbitrary address. It also clears both records in one
      // transaction, so there's no leftover entry for a cleanup pass to chase.
      const { role } = await apiPost(
        `/api/trees/${encodeURIComponent(treeId)}/invites/accept`,
      );

      // Index this tree so future logins use the fast subcollection path
      setDoc(doc(db, 'users', user.uid, 'trees', treeId), { name: treeName, role }).catch(indexWriteFailed('accept'));

      // Update both state and ref so the tree appears in the switcher immediately
      setUserTrees(prev => {
        if (prev.find(t => t.treeId === treeId)) return prev;
        const updated = [...prev, { treeId, name: treeName, role }];
        userTreesRef.current = updated;
        return updated;
      });

      track('invite_accepted', { via: 'notification', role });
      return true;
    } catch (e) {
      console.warn('Could not accept invite:', e);
      return false;
    }
  }, [user]);

  const dismissIncomingInvite = useCallback((treeId) => {
    // Hide the notification without accepting — it will reappear on next load
    setInviteNotifications(prev => prev.filter(n => n.treeId !== treeId));
  }, []);

  const batchUpdatePeople = useCallback(async (updates) => {
    // updates: [{ personId, data }] — applied as one atomic server-side batch,
    // so rewiring a relationship across several people can't half-apply.
    if (!activeTreeId || !updates?.length) return;
    await apiPatch(
      `/api/trees/${encodeURIComponent(activeTreeId)}/people`, { updates },
    );
  }, [activeTreeId]);

  const renameTree = useCallback(async (name) => {
    if (!activeTreeId) return;
    if (userRole !== 'owner') throw new Error('Only the tree owner can rename this tree.');
    const trimmed = name.trim();
    if (!trimmed) return;
    await apiPatch(`/api/trees/${encodeURIComponent(activeTreeId)}`, { name: trimmed });
    // Keep the owner's own subcollection entry in sync.
    // Members' entries stay stale until their next login (the live tree-doc
    // listener already updates their in-session userTrees name via setUserTrees).
    if (user) {
      setDoc(doc(db, 'users', user.uid, 'trees', activeTreeId), { name: trimmed }, { merge: true }).catch(indexWriteFailed('rename'));
    }
  }, [activeTreeId, userRole, user]);

  // Creating a tree from inside the app, rather than only at bootstrap.
  //
  // needsStarterTree mints one only for an account whose tree list is empty,
  // which deliberately excludes anyone who arrived through an invitation link —
  // they were joining a tree, not starting one. That left the invitee who
  // *later* decides they want their own with no way to say so: the switcher
  // listed the tree they had been invited to and nothing else. This is the same
  // POST bootstrap makes, reached by choice instead of by inference.
  //
  // The server caps trees per account and rejects a blank name, so both are left
  // to it rather than duplicated here — the cap in particular is counted from
  // Firestore and cannot be second-guessed from the client.
  const createTree = useCallback(async (name) => {
    if (!user) throw new Error('You must be signed in to create a tree.');
    const trimmed = (name || '').trim() || defaultTreeName(user.displayName);
    const { treeId: newId } = await apiPost('/api/trees', { name: trimmed });

    // Index it so the next login takes the fast subcollection path. Same
    // fire-and-forget contract as everywhere else: a failed write costs a slower
    // login, not the tree.
    setDoc(doc(db, 'users', user.uid, 'trees', newId), { name: trimmed, role: 'owner' }).catch(indexWriteFailed('create'));

    // State and ref together, so the switcher shows the tree in the same tick
    // that switchTree makes it active — otherwise the header renders 'Select
    // tree' until the next update lands.
    setUserTrees(prev => {
      if (prev.find(t => t.treeId === newId)) return prev;
      const updated = [...prev, { treeId: newId, name: trimmed, role: 'owner' }];
      userTreesRef.current = updated;
      return updated;
    });

    switchTree(newId);
    track('tree_created', { tree_count: userTreesRef.current.length, via: 'chosen' });
    return newId;
  }, [user, switchTree]);

  // TreePeopleContext — holds frequently-changing Firestore data.
  // Components that only need people/attachments subscribe here and are isolated
  // from metadata changes (tree name, members, role) and vice-versa.
  const peopleValue = useMemo(() => ({ people, attachments }), [people, attachments]);

  // TreeContext — metadata + actions only (no people/attachments).
  // Memoized so that tree-data updates don't cascade into unrelated consumers
  // like MembersPanel or InviteNotification.
  const value = useMemo(() => ({
    // treeId kept as alias for activeTreeId so existing component code still works
    treeId: activeTreeId,
    activeTreeId,
    userTrees,
    userRole,
    switchTree,
    treeMembers,
    memberDetails,
    pendingInvites,
    loadingTree,
    peopleCount,
    storageBytes,
    editablePersonIds,
    addPerson, updatePerson, deletePerson, bulkCreatePeople,
    uploadAttachment, deleteAttachment, setAttachmentVisibility,
    uploadPersonPhoto, deletePersonPhoto,
    inviteNotifications, acceptIncomingInvite, acceptInviteLink, dismissIncomingInvite,
    inviteJoinError, dismissInviteJoinError,
    inviteMember, revokeInvite, updateMemberRole, removeMember, renameTree, batchUpdatePeople,
    createTree, detachTree,
  }), [
    activeTreeId, userTrees, userRole, switchTree,
    treeMembers, memberDetails, pendingInvites, loadingTree, peopleCount, storageBytes, editablePersonIds,
    addPerson, updatePerson, deletePerson, bulkCreatePeople,
    uploadAttachment, deleteAttachment, setAttachmentVisibility,
    uploadPersonPhoto, deletePersonPhoto,
    inviteNotifications, acceptIncomingInvite, acceptInviteLink, dismissIncomingInvite,
    inviteJoinError, dismissInviteJoinError,
    inviteMember, revokeInvite, updateMemberRole, removeMember, renameTree, batchUpdatePeople,
    createTree, detachTree,
  ]);

  return (
    <TreeContext.Provider value={value}>
      <TreePeopleContext.Provider value={peopleValue}>
        {children}
      </TreePeopleContext.Provider>
    </TreeContext.Provider>
  );
}
